<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import QueueRow from './QueueRow.vue';
import QueueCard from './QueueCard.vue';
import QueuePaginationControls from './QueuePaginationControls.vue';
import InputClearButton from '@/components/ui/InputClearButton.vue';
import QueueFilterCombobox from './QueueFilterCombobox.vue';
import type { QueueRowData } from './queueRow';
import { mapAmlReviewToRow } from './mapAmlReviewToRow';
import { toApiStatus, type QueueStatus } from './queueStatus';
import { useImmersveApiClient } from '@/hooks/useImmersveApiClient';
import {
  AML_REVIEW_PARTNERS,
  AML_REVIEW_PROVIDERS,
  AML_REVIEW_REGIONS,
  AML_REVIEW_SINCE_OPTIONS,
} from '@/integrations/Immersve/amlReviewFilterOptions';
import { useMediaQuery } from '@/util/useMediaQuery';

const TEXT_FILTER_DEBOUNCE_MS = 300;
// Below Tailwind's `md` breakpoint, the table becomes stacked cards instead
// of columns — a genuine JS-driven choice (not just CSS-hidden) so the two
// representations never both exist in the DOM at once. CSS alone would
// leave the hidden one still findable by generic text locators (a real
// Playwright "strict mode" failure caught live) and still tab-focusable.
const isMobile = useMediaQuery('(max-width: 767px)');

const props = defineProps<{ status: QueueStatus }>();

const PAGE_SIZE = 25;
const DEFAULT_SINCE = 'all';

const rows = ref<QueueRowData[]>([]);
const loading = ref(true);
const error = ref<Error | null>(null);

const route = useRoute();
const router = useRouter();

function queryParam(key: string): string {
  const value = route.query[key];
  return typeof value === 'string' ? value : '';
}

const FILTER_QUERY_KEY_FIELD: Record<string, 'idSearch' | 'partner' | 'region' | 'provider'> = {
  accountId: 'idSearch',
  externalId: 'idSearch',
  partner: 'partner',
  region: 'region',
  kycProvider: 'provider',
};

function parseExclusiveFilters(): {
  idSearch: string;
  partner: string;
  region: string;
  provider: string;
} {
  const empty = { idSearch: '', partner: '', region: '', provider: '' };
  const firstFilterKey = Object.keys(route.query).find(
    (key) => key in FILTER_QUERY_KEY_FIELD && queryParam(key)
  );
  if (!firstFilterKey) return empty;
  return { ...empty, [FILTER_QUERY_KEY_FIELD[firstFilterKey]]: queryParam(firstFilterKey) };
}

const initialFilters = parseExclusiveFilters();
const regionFilter = ref(initialFilters.region);
const partnerFilter = ref(initialFilters.partner);
const providerFilter = ref(initialFilters.provider);
// accountIds are always exactly 32 lowercase hex chars; external ids never are.
const ACCOUNT_ID_PATTERN = /^[0-9a-f]{32}$/i;
const idSearchFilter = ref(initialFilters.idSearch);
const idSearchInput = ref(idSearchFilter.value);
const accountIdFilter = computed(() =>
  ACCOUNT_ID_PATTERN.test(idSearchFilter.value) ? idSearchFilter.value : ''
);
const externalIdFilter = computed(() =>
  idSearchFilter.value && !ACCOUNT_ID_PATTERN.test(idSearchFilter.value) ? idSearchFilter.value : ''
);
const sinceFilter = ref(queryParam('since') || DEFAULT_SINCE);
const sortOrder = ref<'asc' | 'desc'>(queryParam('sort') === 'desc' ? 'desc' : 'asc');
const hasActiveFilter = computed(
  () =>
    !!regionFilter.value ||
    !!partnerFilter.value ||
    !!providerFilter.value ||
    !!idSearchFilter.value ||
    sinceFilter.value !== DEFAULT_SINCE
);
// id/partner/region/provider are mutually exclusive (one dimension); since is independent.
const activeFilterCount = computed(
  () =>
    (regionFilter.value || partnerFilter.value || providerFilter.value || idSearchFilter.value
      ? 1
      : 0) + (sinceFilter.value !== DEFAULT_SINCE ? 1 : 0)
);

const partnerDisabled = computed(
  () => !!regionFilter.value || !!providerFilter.value || !!idSearchFilter.value
);
const regionDisabled = computed(
  () => !!partnerFilter.value || !!providerFilter.value || !!idSearchFilter.value
);
const providerDisabled = computed(
  () => !!regionFilter.value || !!partnerFilter.value || !!idSearchFilter.value
);
const idSearchDisabled = computed(
  () => !!regionFilter.value || !!partnerFilter.value || !!providerFilter.value
);

const partnerOptions = computed(() =>
  AML_REVIEW_PARTNERS.map((partner) => ({ value: partner.accountId, label: partner.label }))
);
const regionOptions = computed(() =>
  AML_REVIEW_REGIONS.map((region) => ({ value: region.code, label: region.label }))
);
const providerOptions = computed(() =>
  AML_REVIEW_PROVIDERS.map((provider) => ({ value: provider.value, label: provider.label }))
);
const sinceOptions = computed(() =>
  AML_REVIEW_SINCE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))
);
const sortOptions = [
  { value: 'asc', label: 'Oldest first' },
  { value: 'desc', label: 'Newest first' },
];

let idSearchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
watch(idSearchInput, (value) => {
  clearTimeout(idSearchDebounceTimer);
  idSearchDebounceTimer = setTimeout(() => {
    idSearchFilter.value = value.trim();
  }, TEXT_FILTER_DEBOUNCE_MS);
});

// since is a relative window ('24h', '7d', ...), not a fixed instant — resolve
// it against the current time on every fetch rather than once when selected.
function resolveSince(): string | undefined {
  const hours = AML_REVIEW_SINCE_OPTIONS.find(
    (option) => option.value === sinceFilter.value
  )?.hours;
  return hours === undefined ? undefined : new Date(Date.now() - hours * 3600_000).toISOString();
}

function clearIdSearch() {
  idSearchInput.value = '';
  idSearchFilter.value = '';
}

function clearFilters() {
  regionFilter.value = '';
  partnerFilter.value = '';
  providerFilter.value = '';
  idSearchInput.value = '';
  idSearchFilter.value = '';
  sinceFilter.value = DEFAULT_SINCE;
}

function parseCursorHistory(): (string | undefined)[] {
  const raw = queryParam('cursor');
  return raw ? [undefined, raw] : [undefined];
}

function serializeCursorHistory(history: (string | undefined)[]): string {
  return history[history.length - 1] ?? '';
}

const cursorHistory = ref<(string | undefined)[]>(parseCursorHistory());
const nextCursor = ref<string | undefined>(undefined);
const hasPrevious = computed(() => cursorHistory.value.length > 1);
const hasNext = computed(() => !!nextCursor.value);
// Shown inline on the status-tabs row by the parent page — see defineExpose below.
const showTopPager = computed(() => !error.value && !isMobile.value);
const rowsLabel = computed(() => {
  if (loading.value || error.value) return undefined;
  const count = rows.value.length;
  return count === 0 ? 'No results' : `${count} AML review${count === 1 ? '' : 's'} on this page`;
});

function syncQueryToRoute() {
  const query = { ...route.query };
  if (regionFilter.value) query.region = regionFilter.value;
  else delete query.region;
  if (partnerFilter.value) query.partner = partnerFilter.value;
  else delete query.partner;
  if (providerFilter.value) query.kycProvider = providerFilter.value;
  else delete query.kycProvider;
  if (accountIdFilter.value) query.accountId = accountIdFilter.value;
  else delete query.accountId;
  if (externalIdFilter.value) query.externalId = externalIdFilter.value;
  else delete query.externalId;
  if (sinceFilter.value !== DEFAULT_SINCE) query.since = sinceFilter.value;
  else delete query.since;
  if (sortOrder.value !== 'asc') query.sort = sortOrder.value;
  else delete query.sort;
  const serializedCursor = serializeCursorHistory(cursorHistory.value);
  if (serializedCursor) query.cursor = serializedCursor;
  else delete query.cursor;
  router.replace({ query });
}

async function fetchRows() {
  loading.value = true;
  error.value = null;
  try {
    const response = await useImmersveApiClient().getAmlReviews({
      status: toApiStatus(props.status),
      limit: PAGE_SIZE,
      cursor: cursorHistory.value[cursorHistory.value.length - 1],
      region: regionFilter.value || undefined,
      partnerAccountId: partnerFilter.value || undefined,
      kycProvider: providerFilter.value || undefined,
      accountId: accountIdFilter.value || undefined,
      externalProfileId: externalIdFilter.value || undefined,
      since: resolveSince(),
      sort: sortOrder.value === 'asc' ? undefined : sortOrder.value,
    });
    rows.value = response.items.map(mapAmlReviewToRow);
    nextCursor.value = response.pageInfo?.nextCursor;
  } catch (err) {
    error.value = err as Error;
  } finally {
    loading.value = false;
  }
}

function goToNextPage() {
  if (!nextCursor.value) return;
  cursorHistory.value = [...cursorHistory.value, nextCursor.value];
}

function goToPreviousPage() {
  if (!hasPrevious.value) return;
  cursorHistory.value = cursorHistory.value.slice(0, -1);
}

function resetPagination() {
  cursorHistory.value = [undefined];
  nextCursor.value = undefined;
}

onMounted(() => {
  syncQueryToRoute();
  fetchRows();
});
watch(() => props.status, resetPagination);
watch(
  [regionFilter, partnerFilter, providerFilter, idSearchFilter, sinceFilter, sortOrder],
  resetPagination
);
watch(cursorHistory, () => {
  syncQueryToRoute();
  fetchRows();
});
watch(
  () => route.query,
  () => {
    const filters = parseExclusiveFilters();
    regionFilter.value = filters.region;
    partnerFilter.value = filters.partner;
    providerFilter.value = filters.provider;
    idSearchFilter.value = filters.idSearch;
    idSearchInput.value = idSearchFilter.value;
    sinceFilter.value = queryParam('since') || DEFAULT_SINCE;
    sortOrder.value = queryParam('sort') === 'desc' ? 'desc' : 'asc';

    const routeCursor = queryParam('cursor');
    if (routeCursor !== serializeCursorHistory(cursorHistory.value)) {
      cursorHistory.value = parseCursorHistory();
    }
  }
);

// The page renders the top pager itself, inline on the status-tabs row, per the design.
defineExpose({ showTopPager, hasPrevious, hasNext, loading, goToPreviousPage, goToNextPage });
</script>

<template>
  <div class="mb-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
    <div class="grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-center">
      <div class="relative col-span-2 md:grow md:basis-96">
        <MagnifyingGlassIcon
          class="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400"
          aria-hidden="true"
        />
        <input
          v-model="idSearchInput"
          type="text"
          placeholder="Search account or external ID"
          data-test="queue-filter-id-search"
          :disabled="idSearchDisabled"
          class="w-full rounded-lg border border-gray-300 bg-white py-2 pr-9 pl-9 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
        />
        <InputClearButton
          v-if="idSearchInput && !idSearchDisabled"
          label="Clear search"
          data-test="queue-filter-id-search-clear"
          @click="clearIdSearch"
        />
      </div>
      <QueueFilterCombobox
        class="w-full md:w-44 xl:w-36"
        :options="partnerOptions"
        :model-value="partnerFilter"
        all-label="All partners"
        placeholder="Search partners"
        :disabled="partnerDisabled"
        data-test="queue-filter-partner"
        @update:model-value="(value) => (partnerFilter = value)"
      />
      <QueueFilterCombobox
        class="w-full md:w-44 xl:w-36"
        :options="regionOptions"
        :model-value="regionFilter"
        all-label="All countries"
        placeholder="Search countries"
        :disabled="regionDisabled"
        data-test="queue-filter-region"
        @update:model-value="(value) => (regionFilter = value)"
      />
      <QueueFilterCombobox
        class="w-full md:w-40 xl:w-36"
        :options="providerOptions"
        :model-value="providerFilter"
        all-label="All providers"
        :searchable="false"
        :disabled="providerDisabled"
        data-test="queue-filter-provider"
        @update:model-value="(value) => (providerFilter = value)"
      />
      <QueueFilterCombobox
        class="w-full md:w-40 xl:ml-auto xl:w-36"
        :options="sinceOptions"
        :model-value="sinceFilter"
        :include-all-option="false"
        :searchable="false"
        data-test="queue-filter-since"
        @update:model-value="(value) => (sinceFilter = value)"
      />
      <QueueFilterCombobox
        class="col-span-2 w-full md:w-36 xl:w-32"
        :options="sortOptions"
        :model-value="sortOrder"
        :include-all-option="false"
        :searchable="false"
        data-test="queue-filter-sort"
        @update:model-value="(value) => (sortOrder = value as 'asc' | 'desc')"
      />
      <button
        v-if="hasActiveFilter"
        type="button"
        data-test="queue-filter-clear"
        class="col-span-2 text-left text-sm font-medium text-indigo-600 hover:text-indigo-700 md:text-right"
        @click="clearFilters"
      >
        Clear filters ({{ activeFilterCount }})
      </button>
    </div>
  </div>
  <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div
      v-if="!isMobile"
      class="grid min-w-full grid-cols-[2fr_1fr_1.4fr_1fr] bg-gray-50 text-xs font-semibold tracking-wide text-gray-500 uppercase lg:grid-cols-[2.2fr_1fr_0.9fr_1.3fr_0.9fr_36px] xl:grid-cols-[2.2fr_1fr_0.8fr_0.6fr_1.2fr_0.8fr_40px]"
    >
      <div class="px-4 py-2">Profile</div>
      <div class="px-4 py-2">Partner</div>
      <div class="hidden px-4 py-2 lg:block">Provider</div>
      <div class="hidden px-4 py-2 xl:block">Country</div>
      <div class="px-4 py-2">Open hit types</div>
      <div class="px-4 py-2 text-center">Age in queue</div>
      <div class="hidden px-2 py-2 lg:block" aria-hidden="true"></div>
    </div>
    <div
      v-if="loading"
      data-test="queue-loading"
      class="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-500"
    >
      <ArrowPathIcon class="h-4 w-4 animate-spin" />
      Loading…
    </div>
    <p v-else-if="error" data-test="queue-error" class="px-4 py-6 text-sm text-red-600">
      {{ error.message }}
    </p>
    <div
      v-else-if="!isMobile"
      class="grid min-w-full grid-cols-[2fr_1fr_1.4fr_1fr] lg:grid-cols-[2.2fr_1fr_0.9fr_1.3fr_0.9fr_36px] xl:grid-cols-[2.2fr_1fr_0.8fr_0.6fr_1.2fr_0.8fr_40px]"
    >
      <QueueRow v-for="row in rows" :key="row.id" :row="row" />
    </div>
    <div v-else class="flex flex-col gap-2.5 p-3">
      <QueueCard v-for="row in rows" :key="row.id" :row="row" />
    </div>
    <p
      v-if="!loading && !error && rows.length === 0 && !isMobile"
      data-test="queue-empty"
      class="px-4 py-14 text-center text-sm text-gray-500"
    >
      No AML reviews match these filters.
    </p>
    <p
      v-if="!loading && !error && rows.length === 0 && isMobile"
      data-test="queue-empty"
      class="m-3 rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500"
    >
      No AML reviews match these filters.
    </p>
    <QueuePaginationControls
      v-if="!error"
      position="bottom"
      :variant="isMobile ? 'split' : 'bar'"
      :rows-label="isMobile ? undefined : rowsLabel"
      :has-previous="hasPrevious"
      :has-next="hasNext"
      :loading="loading"
      @previous="goToPreviousPage"
      @next="goToNextPage"
    />
  </div>
</template>
